import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { sr, auth } from "../../lib/sr";
import { timeAgo } from "../../lib/timeAgo";
import {
  Loader2, Lock, Percent, Layers, ShieldCheck, Target, Gift, Sparkles,
  Calculator, Info, Plus, Trash2, Save, History, Users, AlertTriangle, RotateCcw,
} from "lucide-react";

const ADMIN_ROLES = ["admin", "super_admin"];
const usd = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const usd2 = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Gross margin % implied by a markup-on-cost %
const grossMarginFromMarkup = (markupPct) => {
  const m = Number(markupPct) || 0;
  return (100 + m) ? (m / (100 + m)) * 100 : 0;
};

function Field({ label, value, onChange, step = "1", prefix, suffix, testid, hint }) {
  return (
    <div className="sr-field" style={{ marginBottom: 0 }}>
      <label className="sr-label">{label}</label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {prefix && <span style={{ position: "absolute", left: 12, color: "var(--muted)", fontWeight: 700 }}>{prefix}</span>}
        <input
          className="sr-input" type="number" step={step} value={value ?? ""}
          onChange={(e) => onChange(e.target.value)} data-testid={testid}
          style={{ paddingLeft: prefix ? 26 : 14, paddingRight: suffix ? 30 : 14 }}
        />
        {suffix && <span style={{ position: "absolute", right: 12, color: "var(--muted)", fontWeight: 700 }}>{suffix}</span>}
      </div>
      {hint && <div className="sr-muted" style={{ fontSize: 11.5, marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children, onSave, saving, testid, saveTestid }) {
  return (
    <div className="sr-card" data-testid={testid} style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--navy)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon size={17} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: "var(--navy)", fontWeight: 800 }}>{title}</h3>
            {subtitle && <p className="sr-muted" style={{ margin: "3px 0 0", fontSize: 12.5, maxWidth: 560 }}>{subtitle}</p>}
          </div>
        </div>
        {onSave && (
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={onSave} disabled={saving} data-testid={saveTestid}>
            {saving ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save</>}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 };

export default function PricingEngineEmbed() {
  const me = auth.getUser();
  const isAdmin = me && ADMIN_ROLES.includes(me.role);

  const [settings, setSettings] = useState(null);
  const [rates, setRates] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [adGenPrice, setAdGenPrice] = useState("");
  const [history, setHistory] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [ovTenant, setOvTenant] = useState("");
  const [ovPct, setOvPct] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [calcBudget, setCalcBudget] = useState(5000);

  const loadHistory = () => sr.pricingHistory().then((h) => setHistory(h.history || [])).catch(() => {});
  const loadOverrides = () => sr.clientOverrides().then((o) => setOverrides(o.overrides || [])).catch(() => {});

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    (async () => {
      try {
        const [s, r, b, c, h, o, t] = await Promise.all([
          sr.pricingSettings().catch(() => null),
          sr.premiumRates().catch(() => null),
          sr.bonusConfig().catch(() => null),
          sr.creditConfig().catch(() => null),
          sr.pricingHistory().catch(() => null),
          sr.clientOverrides().catch(() => null),
          sr.adminTenants().catch(() => null),
        ]);
        if (s) setSettings(s);
        if (r) setRates(r);
        if (b) setTiers(b.tiers || []);
        if (c) setAdGenPrice(c.ad_gen_price);
        if (h) setHistory(h.history || []);
        if (o) setOverrides(o.overrides || []);
        if (t) setTenants(t.tenants || []);
      } finally { setLoading(false); }
    })();
  }, [isAdmin]);

  const setS = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  const saveSettings = async (section) => {
    setSaving(section);
    try {
      const payload = {
        base_platform_fee: Number(settings.base_platform_fee) || 0,
        default_media_markup_pct: Number(settings.default_media_markup_pct) || 0,
        management_fee_pct: Number(settings.management_fee_pct) || 0,
        launch_min_monthly_spend: Number(settings.launch_min_monthly_spend) || 0,
        grow_min_monthly_spend: Number(settings.grow_min_monthly_spend) || 0,
        dominate_min_monthly_spend: Number(settings.dominate_min_monthly_spend) || 0,
        dominate_media_margin_discount_pct: Number(settings.dominate_media_margin_discount_pct) || 0,
        dominate_discount_method: settings.dominate_discount_method || "relative",
        min_promo_margin_pct: Number(settings.min_promo_margin_pct) || 0,
        min_promo_margin_dollars: Number(settings.min_promo_margin_dollars) || 0,
        expert_assist_min_monthly_spend: Number(settings.expert_assist_min_monthly_spend) || 0,
        expert_assist_min_contract_months: Number(settings.expert_assist_min_contract_months) || 0,
      };
      const res = await sr.savePricingSettings(payload);
      setSettings(res);
      toast.success("Pricing settings saved");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save (Super Admin required)");
    } finally { setSaving(""); }
  };

  const saveRates = async () => {
    setSaving("rates");
    try {
      const res = await sr.savePremiumRates({
        event_venue_pct: Number(rates.event_venue_pct) || 0,
        foot_traffic_pct: Number(rates.foot_traffic_pct) || 0,
      });
      setRates(res);
      toast.success("Premium add-on rates saved");
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(""); }
  };

  const saveTiers = async () => {
    setSaving("tiers");
    try {
      const clean = tiers.map((t) => ({
        from: Number(t.from) || 0,
        to: t.to === "" || t.to === null || t.to === undefined ? null : Number(t.to),
        rate: (Number(t.ratePct) || 0) / 100,
      }));
      const res = await sr.saveBonusConfig(clean);
      setTiers((res.tiers || clean).map((t) => ({ ...t, ratePct: Math.round((t.rate || 0) * 1000) / 10 })));
      toast.success("Bonus inventory tiers saved");
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save (staff only)"); }
    finally { setSaving(""); }
  };

  const saveAiPrice = async () => {
    setSaving("ai");
    try {
      const res = await sr.saveCreditConfig(Number(adGenPrice) || 0);
      setAdGenPrice(res.ad_gen_price);
      toast.success("AI ad credit price saved");
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save (staff only)"); }
    finally { setSaving(""); }
  };

  const saveOverride = async (clear) => {
    if (!ovTenant) { toast.error("Pick a client first"); return; }
    setSaving("override");
    try {
      await sr.saveClientOverride(ovTenant, clear ? null : (Number(ovPct) || 0));
      await loadOverrides();
      loadHistory();
      if (clear) { setOvPct(""); }
      toast.success(clear ? "Override removed" : "Client override saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(""); }
  };

  const clearOverrideFor = async (tid) => {
    try {
      await sr.saveClientOverride(tid, null);
      await loadOverrides();
      loadHistory();
      toast.success("Override removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const revert = async (hid) => {
    setSaving("revert-" + hid);
    try {
      await sr.revertPricing(hid);
      const [s, r, b, c] = await Promise.all([
        sr.pricingSettings().catch(() => null), sr.premiumRates().catch(() => null),
        sr.bonusConfig().catch(() => null), sr.creditConfig().catch(() => null),
      ]);
      if (s) setSettings(s);
      if (r) setRates(r);
      if (b) setTiers(b.tiers || []);
      if (c) setAdGenPrice(c.ad_gen_price);
      await Promise.all([loadHistory(), loadOverrides()]);
      toast.success("Reverted to the earlier value");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revert"); }
    finally { setSaving(""); }
  };

  const tiersView = useMemo(
    () => (tiers || []).map((t) => ({ ...t, ratePct: t.ratePct ?? Math.round((t.rate || 0) * 1000) / 10 })),
    [tiers]
  );

  const calc = useMemo(() => {
    if (!settings) return null;
    const b = Number(calcBudget) || 0;
    const markup = Number(settings.default_media_markup_pct) || 0;
    const dMin = Number(settings.dominate_min_monthly_spend) || 0;
    const gMin = Number(settings.grow_min_monthly_spend) || 0;
    const tier = b >= dMin ? "Dominate" : b >= gMin ? "Grow" : "Launch";
    let effMarkup = markup;
    if (tier === "Dominate") {
      const d = Number(settings.dominate_media_margin_discount_pct) || 0;
      effMarkup = settings.dominate_discount_method === "absolute" ? markup - d : markup * (1 - d / 100);
    }
    const dspCost = effMarkup > -100 ? b / (1 + effMarkup / 100) : b;
    const mediaMargin = b - dspCost;
    const mgmtFee = b * (Number(settings.management_fee_pct) || 0) / 100;
    const total = b + mgmtFee;
    const grossPct = b ? (mediaMargin / b) * 100 : 0;
    const expertEligible = b >= (Number(settings.expert_assist_min_monthly_spend) || 0);
    return { b, tier, effMarkup, dspCost, mediaMargin, mgmtFee, total, grossPct, expertEligible };
  }, [settings, calcBudget]);

  const floorPct = Number(settings?.min_promo_margin_pct) || 0;

  const tierPreview = useMemo(() => {
    if (!settings) return [];
    const markup = Number(settings.default_media_markup_pct) || 0;
    const dDisc = Number(settings.dominate_media_margin_discount_pct) || 0;
    const domMarkup = settings.dominate_discount_method === "absolute" ? markup - dDisc : markup * (1 - dDisc / 100);
    const defs = [
      { key: "Launch", min: Number(settings.launch_min_monthly_spend) || 0, markup },
      { key: "Grow", min: Number(settings.grow_min_monthly_spend) || 0, markup },
      { key: "Dominate", min: Number(settings.dominate_min_monthly_spend) || 0, markup: domMarkup },
    ];
    return defs.map((d) => {
      const b = d.min;
      const dspCost = d.markup > -100 ? b / (1 + d.markup / 100) : b;
      const gm = grossMarginFromMarkup(d.markup);
      return { ...d, dspCost, mediaMargin: b - dspCost, gm, belowFloor: gm < floorPct, mgmtFee: b * (Number(settings.management_fee_pct) || 0) / 100 };
    });
  }, [settings, floorPct]);

  const guardrail = tierPreview.find((t) => t.belowFloor);

  if (!isAdmin) {
    return (
      <div className="sr-card" data-testid="pricing-access-denied" style={{ textAlign: "center", padding: 48 }}>
        <Lock size={28} style={{ color: "var(--muted)" }} />
        <h3 style={{ margin: "12px 0 4px", color: "var(--navy)" }}>Admins only</h3>
        <p className="sr-muted" style={{ maxWidth: 440, margin: "0 auto" }}>
          The Pricing Engine — media margin, spend tiers, discounts and add-on markups — is visible to platform admins only.
        </p>
      </div>
    );
  }
  if (loading) return <div className="sr-empty" data-testid="pricing-loading"><Loader2 className="sr-spin" /></div>;

  return (
    <div data-testid="smartreach-pricing-engine">
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: ".08em", color: "var(--accent-strong)", textTransform: "uppercase" }}>
          Internal commercial config · Admins only
        </div>
        <h1 style={{ margin: "4px 0 2px", color: "var(--navy)", fontSize: 26, fontWeight: 800 }}>Pricing Engine</h1>
        <p className="sr-muted" style={{ margin: 0, maxWidth: 680 }}>
          How SmartReach prices media and services: the media margin, spend-unlocked tiers and volume discount, the discount
          floor, premium add-on markups and the AI credit price. Clients only ever see the SmartReach price — cost and margin
          are never exposed to them.
        </p>
      </div>

      {guardrail && (
        <div className="sr-card" data-testid="pricing-guardrail-alert" style={{ marginBottom: 18, background: "#FEF2F2", border: "1px solid #F3B4B4", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <AlertTriangle size={18} style={{ color: "#C0392B", flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: "var(--navy)" }}>
            <strong>Below your discount floor.</strong> At the <strong>{guardrail.key}</strong> tier the effective gross margin is{" "}
            <strong>{guardrail.gm.toFixed(1)}%</strong>, under your {floorPct}% minimum. Raise the media margin or lower the Dominate discount.
          </div>
        </div>
      )}

      {settings && (
        <>
          <SectionCard
            icon={Percent} title="Media margin & fees"
            subtitle="Our markup on DSP media cost. Client media price = DSP cost × (1 + margin). A 50% margin ≈ 33% gross margin on client media revenue."
            onSave={() => saveSettings("margin")} saving={saving === "margin"}
            testid="pricing-card-margin" saveTestid="save-margin-btn"
          >
            <div style={grid}>
              <Field label="Media margin" suffix="%" value={settings.default_media_markup_pct} onChange={(v) => setS("default_media_markup_pct", v)} testid="field-media-markup" hint="Applied to all media by default" />
              <Field label="Easy-mode management fee" suffix="%" value={settings.management_fee_pct} onChange={(v) => setS("management_fee_pct", v)} testid="field-management-fee" hint="Added on top of media in the Easy journey" />
              <Field label="Base platform fee" prefix="$" value={settings.base_platform_fee} onChange={(v) => setS("base_platform_fee", v)} testid="field-base-fee" hint="Flat monthly fee (0 = none)" />
            </div>
          </SectionCard>

          <SectionCard
            icon={Layers} title="Spend tiers & volume discount"
            subtitle="Monthly committed media unlocks Launch → Grow → Dominate. Top-tier clients get a discount on the media margin."
            onSave={() => saveSettings("tiers")} saving={saving === "tiers"}
            testid="pricing-card-spend-tiers" saveTestid="save-spend-tiers-btn"
          >
            <div style={grid}>
              <Field label="Launch — min / mo" prefix="$" step="100" value={settings.launch_min_monthly_spend} onChange={(v) => setS("launch_min_monthly_spend", v)} testid="field-launch-min" />
              <Field label="Grow — min / mo" prefix="$" step="100" value={settings.grow_min_monthly_spend} onChange={(v) => setS("grow_min_monthly_spend", v)} testid="field-grow-min" />
              <Field label="Dominate — min / mo" prefix="$" step="100" value={settings.dominate_min_monthly_spend} onChange={(v) => setS("dominate_min_monthly_spend", v)} testid="field-dominate-min" />
            </div>
            <div style={{ ...grid, marginTop: 16 }}>
              <Field label="Dominate margin discount" suffix="%" step="0.5" value={settings.dominate_media_margin_discount_pct} onChange={(v) => setS("dominate_media_margin_discount_pct", v)} testid="field-dominate-discount" />
              <div className="sr-field" style={{ marginBottom: 0 }}>
                <label className="sr-label">Discount method</label>
                <select className="sr-select" value={settings.dominate_discount_method || "relative"} onChange={(e) => setS("dominate_discount_method", e.target.value)} data-testid="field-discount-method">
                  <option value="relative">Relative — cut margin by X% (50% → 45%)</option>
                  <option value="absolute">Absolute — subtract X points (50% → 40%)</option>
                </select>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={ShieldCheck} title="Discount floor & Expert Assist"
            subtitle="The lowest gross margin any promotion or credit may leave, plus who qualifies for a dedicated strategist."
            onSave={() => saveSettings("floor")} saving={saving === "floor"}
            testid="pricing-card-floor" saveTestid="save-floor-btn"
          >
            <div style={grid}>
              <Field label="Minimum promo margin" suffix="%" step="0.5" value={settings.min_promo_margin_pct} onChange={(v) => setS("min_promo_margin_pct", v)} testid="field-min-margin-pct" hint="Promotions can't push margin below this" />
              <Field label="Minimum promo margin" prefix="$" value={settings.min_promo_margin_dollars} onChange={(v) => setS("min_promo_margin_dollars", v)} testid="field-min-margin-dollars" hint="Absolute dollar floor (0 = none)" />
              <Field label="Expert Assist — min / mo" prefix="$" step="100" value={settings.expert_assist_min_monthly_spend} onChange={(v) => setS("expert_assist_min_monthly_spend", v)} testid="field-expert-min-spend" />
              <Field label="Expert Assist — min months" value={settings.expert_assist_min_contract_months} onChange={(v) => setS("expert_assist_min_contract_months", v)} testid="field-expert-min-months" />
            </div>
          </SectionCard>
        </>
      )}

      {rates && (
        <SectionCard
          icon={Target} title="Premium add-on markups"
          subtitle="Optional targeting upgrades, each a % of eligible media. They never compound (max +20% combined)."
          onSave={saveRates} saving={saving === "rates"}
          testid="pricing-card-premiums" saveTestid="save-premiums-btn"
        >
          <div style={grid}>
            <Field label="Event & Venue Targeting" suffix="%" step="0.5" value={rates.event_venue_pct} onChange={(v) => setRates((p) => ({ ...p, event_venue_pct: v }))} testid="field-event-venue" />
            <Field label="Foot Traffic Attribution" suffix="%" step="0.5" value={rates.foot_traffic_pct} onChange={(v) => setRates((p) => ({ ...p, foot_traffic_pct: v }))} testid="field-foot-traffic" />
          </div>
        </SectionCard>
      )}

      {tiers && (
        <SectionCard
          icon={Gift} title="Bonus inventory tiers"
          subtitle="Prospective bonus media earned as annual delivery grows (marginal bands, rolling 12 months, never compounds). Not a cash credit."
          onSave={saveTiers} saving={saving === "tiers-bonus"}
          testid="pricing-card-bonus" saveTestid="save-bonus-btn"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tiersView.map((t, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 10, alignItems: "end" }} data-testid={`bonus-tier-${i}`}>
                <Field label={i === 0 ? "From ($ delivered)" : ""} prefix="$" step="1000" value={t.from} onChange={(v) => setTiers((p) => p.map((x, j) => j === i ? { ...x, from: v } : x))} testid={`bonus-from-${i}`} />
                <Field label={i === 0 ? "To ($, blank = ∞)" : ""} prefix="$" step="1000" value={t.to ?? ""} onChange={(v) => setTiers((p) => p.map((x, j) => j === i ? { ...x, to: v } : x))} testid={`bonus-to-${i}`} />
                <Field label={i === 0 ? "Bonus rate" : ""} suffix="%" step="0.5" value={t.ratePct} onChange={(v) => setTiers((p) => p.map((x, j) => j === i ? { ...x, ratePct: v } : x))} testid={`bonus-rate-${i}`} />
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setTiers((p) => p.filter((_, j) => j !== i))} data-testid={`bonus-remove-${i}`} style={{ padding: 9 }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setTiers((p) => [...p, { from: 0, to: null, ratePct: 0 }])} data-testid="bonus-add-tier" style={{ alignSelf: "flex-start" }}>
              <Plus size={14} /> Add tier
            </button>
          </div>
        </SectionCard>
      )}

      {adGenPrice !== "" && (
        <SectionCard
          icon={Sparkles} title="AI ad credit price"
          subtitle="What one AI ad generation costs in SmartReach Credits (after a client's 30-day free window)."
          onSave={saveAiPrice} saving={saving === "ai"}
          testid="pricing-card-ai" saveTestid="save-ai-btn"
        >
          <div style={grid}>
            <Field label="Price per AI ad generation" prefix="$" step="0.01" value={adGenPrice} onChange={setAdGenPrice} testid="field-ad-gen-price" />
          </div>
        </SectionCard>
      )}

      {/* Tier preview */}
      {tierPreview.length > 0 && (
        <div className="sr-card" data-testid="pricing-tier-preview" style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--navy)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><Layers size={17} /></div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, color: "var(--navy)", fontWeight: 800 }}>Tier preview</h3>
              <p className="sr-muted" style={{ margin: "3px 0 0", fontSize: 12.5 }}>What each tier does to margin, at that tier's minimum monthly media.</p>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
            {tierPreview.map((t) => (
              <div key={t.key} data-testid={`tier-preview-${t.key.toLowerCase()}`} style={{ border: `1px solid ${t.belowFloor ? "#F3B4B4" : "var(--border)"}`, borderRadius: 12, padding: 14, background: t.belowFloor ? "#FEF7F7" : "#fff" }}>
                <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: 15 }}>{t.key}</div>
                <div className="sr-muted" style={{ fontSize: 11.5, marginBottom: 10 }}>from {usd(t.min)}/mo</div>
                <Row k="Effective margin" v={`${t.markup.toFixed(1)}%`} />
                <Row k="Gross margin" v={`${t.gm.toFixed(1)}%`} danger={t.belowFloor} />
                <Row k="Our DSP cost" v={usd(t.dspCost)} />
                <Row k="Media margin" v={usd(t.mediaMargin)} gold />
                <Row k="+ Mgmt fee" v={usd(t.mgmtFee)} />
                {t.belowFloor && <div style={{ fontSize: 11, color: "#C0392B", marginTop: 8, fontWeight: 700 }}>Below {floorPct}% floor</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-client override */}
      <SectionCard
        icon={Users} title="Per-client media margin"
        subtitle="Set a custom media margin for one client on top of the global default. It re-prices that client's audience & moviegoer CPMs; promos and the global default are untouched."
        testid="pricing-card-overrides"
      >
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto auto", gap: 10, alignItems: "end" }}>
          <div className="sr-field" style={{ marginBottom: 0 }}>
            <label className="sr-label">Client</label>
            <select className="sr-select" value={ovTenant} onChange={(e) => setOvTenant(e.target.value)} data-testid="override-client-select">
              <option value="">Select a client…</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
            </select>
          </div>
          <Field label="Media margin" suffix="%" step="0.5" value={ovPct} onChange={setOvPct} testid="override-pct" />
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => saveOverride(false)} disabled={saving === "override"} data-testid="override-save-btn" style={{ height: 44 }}>
            {saving === "override" ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Set</>}
          </button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => saveOverride(true)} disabled={saving === "override" || !ovTenant} data-testid="override-clear-btn" style={{ height: 44 }}>Clear</button>
        </div>
        {ovPct !== "" && grossMarginFromMarkup(ovPct) < floorPct && (
          <div style={{ fontSize: 11.5, color: "#C0392B", marginTop: 8, fontWeight: 700 }} data-testid="override-floor-warn">
            Heads up: {Number(ovPct).toFixed(1)}% markup = {grossMarginFromMarkup(ovPct).toFixed(1)}% gross margin, below your {floorPct}% floor.
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          {overrides.length === 0 && <p className="sr-muted" style={{ fontSize: 12.5, margin: 0 }} data-testid="overrides-empty">No client overrides — everyone uses the global {settings?.default_media_markup_pct}% margin.</p>}
          {overrides.map((o) => (
            <div key={o.tenant_id} data-testid={`override-row-${o.tenant_id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "9px 0", borderTop: "1px solid var(--border)" }}>
              <span style={{ color: "var(--navy)", fontWeight: 700, fontSize: 13.5 }}>{o.tenant_name}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="sr-badge sr-badge-amber" style={{ fontSize: 12 }}>{o.media_markup_pct}% margin</span>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => clearOverrideFor(o.tenant_id)} data-testid={`override-remove-${o.tenant_id}`} style={{ padding: 8 }}><Trash2 size={13} /></button>
              </span>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Change history */}
      <SectionCard
        icon={History} title="Change history"
        subtitle="Every pricing change — who, when and what — with one-click revert to the earlier value."
        testid="pricing-card-history"
      >
        {history.length === 0 && <p className="sr-muted" style={{ fontSize: 12.5, margin: 0 }} data-testid="history-empty">No pricing changes logged yet.</p>}
        <div style={{ display: "flex", flexDirection: "column" }}>
          {history.map((h) => (
            <div key={h.id} data-testid={`history-row-${h.id}`} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "11px 0", borderTop: "1px solid var(--border)" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 13.5 }}>{h.label}</div>
                <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {(h.changes || []).slice(0, 4).map((c, i) => (
                    <span key={i}>{prettyField(c.field)}: {fmtVal(c.from)} → <strong style={{ color: "var(--navy)" }}>{fmtVal(c.to)}</strong>{i < Math.min(h.changes.length, 4) - 1 ? " · " : ""}</span>
                  ))}
                </div>
                <div className="sr-muted" style={{ fontSize: 11, marginTop: 3 }}>{h.actor_email || h.actor_name || "—"} · {timeAgo(h.at)}</div>
              </div>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => revert(h.id)} disabled={saving === "revert-" + h.id} data-testid={`history-revert-${h.id}`} style={{ flexShrink: 0 }}>
                {saving === "revert-" + h.id ? <Loader2 size={13} className="sr-spin" /> : <><RotateCcw size={13} /> Revert</>}
              </button>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Live example calculator */}
      {calc && (
        <div className="sr-card" data-testid="pricing-calculator" style={{ background: "var(--navy)", color: "#fff", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <Calculator size={18} style={{ color: "var(--gold)" }} />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#fff" }}>Live example</h3>
          </div>
          <div style={{ maxWidth: 280, marginBottom: 16 }}>
            <label className="sr-label" style={{ color: "#cdd8e6" }}>Client monthly media budget</label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span style={{ position: "absolute", left: 12, color: "#8aa0b8", fontWeight: 700 }}>$</span>
              <input className="sr-input" type="number" step="100" value={calcBudget} onChange={(e) => setCalcBudget(e.target.value)} data-testid="calc-budget-input" style={{ paddingLeft: 26, background: "#0e2036", color: "#fff", borderColor: "#24405f" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 1, background: "#24405f", borderRadius: 10, overflow: "hidden" }}>
            <CalcStat label="Tier" value={calc.tier} testid="calc-tier" />
            <CalcStat label="Effective margin" value={`${calc.effMarkup.toFixed(1)}%`} testid="calc-eff-markup" />
            <CalcStat label="DSP cost (ours)" value={usd(calc.dspCost)} testid="calc-dsp-cost" />
            <CalcStat label="Media margin" value={usd(calc.mediaMargin)} gold testid="calc-media-margin" />
            <CalcStat label="Mgmt fee" value={usd(calc.mgmtFee)} testid="calc-mgmt-fee" />
            <CalcStat label="Client total" value={usd(calc.total)} testid="calc-total" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 12, color: "#9fb2c8", fontSize: 12.5 }}>
            <Info size={14} />
            <span>Gross margin on media ≈ <strong style={{ color: "#fff" }}>{calc.grossPct.toFixed(1)}%</strong>. Expert Assist: <strong style={{ color: calc.expertEligible ? "var(--gold)" : "#9fb2c8" }}>{calc.expertEligible ? "eligible" : "not yet"}</strong>. Premium add-ons excluded from this example.</span>
          </div>
        </div>
      )}
    </div>
  );
}

const CalcStat = ({ label, value, gold, testid }) => (
  <div style={{ background: "var(--navy)", padding: "12px 14px" }}>
    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em", color: "#8aa0b8" }}>{label}</div>
    <div data-testid={testid} style={{ fontSize: 18, fontWeight: 800, marginTop: 3, color: gold ? "var(--gold)" : "#fff" }}>{value}</div>
  </div>
);

const Row = ({ k, v, gold, danger }) => (
  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "2px 0" }}>
    <span className="sr-muted">{k}</span>
    <span style={{ fontWeight: 700, color: danger ? "#C0392B" : gold ? "var(--accent-strong)" : "var(--navy)" }}>{v}</span>
  </div>
);

const _FIELD_LABELS = {
  default_media_markup_pct: "Media margin", management_fee_pct: "Mgmt fee", base_platform_fee: "Base fee",
  launch_min_monthly_spend: "Launch min", grow_min_monthly_spend: "Grow min", dominate_min_monthly_spend: "Dominate min",
  dominate_media_margin_discount_pct: "Dominate discount", dominate_discount_method: "Discount method",
  min_promo_margin_pct: "Margin floor %", min_promo_margin_dollars: "Margin floor $",
  expert_assist_min_monthly_spend: "Expert Assist min", expert_assist_min_contract_months: "Expert Assist months",
  event_venue_pct: "Event & Venue", foot_traffic_pct: "Foot Traffic", tiers: "Bonus tiers",
  ad_gen_price: "AI ad price", media_markup_pct: "Media margin",
};
const prettyField = (f) => _FIELD_LABELS[f] || String(f).replace(/_/g, " ");
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return `${v.length} tiers`;
  return String(v);
};
